<template>
	<div class="notification-datatable">
		<DataTable
			:definition="definition"
			:endpoint="definition.endpoint"
			presentation="list"
			:hide-header="true"
			use-place-holder-data
			:paginate="{ perPage: 25, rows: [25, 50, 100] }"
			ref="dataTableRef"
		>
			<template #empty>
				<v-list>
					<v-list-item>
						<div class="justify-center text-caption">
							{{
								t('component.notifications.noneFound', {
									status: getModeText(mode),
								})
							}}
						</div>
					</v-list-item>
				</v-list>
			</template>
			<template #list-items="{ items, loading }">
				<v-list
					class="overflow-y-auto"
					density="compact"
					lines="three"
					:max-height="height * 0.6"
				>
					<!-- Loading state -->
					<v-list-item v-if="loading && items.length === 0">
						<v-progress-linear indeterminate />
					</v-list-item>

					<!-- Empty state -->

					<NotificationItems
						:notifications="items"
						:mode="mode"
						@mark-as-read="markAsRead($event)"
						@notification-link="handleNotificationLink($event)"
					/>
				</v-list>
			</template>
		</DataTable>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DataTable from '@/components/templates/Datatable/DataTable.vue'
import NotificationItems from './NotificationItems.vue'
import type { DataTableDefinition } from '@/types/table.ts'
import { useTable } from '@/composables/useTable'
import { useNotificationStore } from '@/stores/modules/notification.ts'
import { useI18n } from 'vue-i18n'
interface Props {
	mode: number
	height: number
	definition: DataTableDefinition
}

const { t } = useI18n()

const props = defineProps<Props>()
const emit = defineEmits<{
	notificationLink: [id: string]
}>()
const dataTableId = defineModel()
const dataTableRef = ref<InstanceType<typeof DataTable> | null>(null)

const { reload } = useTable({
	tableId: dataTableRef.value?.tableId,
	endpoint: props.definition.endpoint,
	disabled: computed(() => true),
	paginate: { perPage: 50, rows: [25, 50, 100] },
})
const notificationStore = useNotificationStore()

function getModeText(mode: number): string {
	switch (mode) {
		case 0:
			return t('component.notifications.new')
		case 1:
			return t('component.notifications.read_notifications')
		default:
			return ''
	}
}

async function markAsRead(id: string) {
	try {
		notificationStore.mark({ id })
		await reload(true)
	} catch (error) {
		console.error('Failed to mark notification as read:', error)
	}
}

function handleNotificationLink(id: string) {
	markAsRead(id)
	emit('notificationLink', id)
}

watch(
	() => dataTableRef?.value?.tableId,
	(tableId) => {
		if (tableId) {
			dataTableId.value = tableId
		}
	},
	{ immediate: true }
)
</script>

<style scoped>
.notification-datatable {
	width: 100%;
}
</style>
